import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { Box, CircularProgress, Typography } from '@mui/material';

interface PrivateRouteProps {
  children: ReactNode;
  requiredPermissions?: string[];
  requiredRoles?: string[];
}

/**
 * Private Route Component
 * Protects routes requiring authentication and optionally specific permissions/roles
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
}) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const {
    hasPermission,
    hasAllPermissions,
    isLoading: permissionLoading,
  } = usePermissions();

  const resolveUserRoleLevel = (role?: string) => {
    const code = String(role || '').toUpperCase();
    if (code === 'SUPER_ADMIN') return 1;
    if (code === 'ADMIN') return 2;
    if (code.startsWith('ADMIN_')) return 3;
    if (code.startsWith('STAFF_') || code === 'STAFF' || code === 'FACULTY' || code === 'TEACHER') return 4;
    if (code === 'STUDENT') return 5;
    return 999;
  };

  const resolveRequiredRoleLevel = (role?: string) => {
    const code = String(role || '').toUpperCase();
    if (code === 'SUPER_ADMIN') return 1;
    if (code === 'ADMIN') return 3;
    if (code === 'STAFF' || code === 'FACULTY' || code === 'TEACHER') return 4;
    if (code === 'STUDENT') return 5;
    return 999;
  };

  // Show loading while checking authentication
  if (authLoading || permissionLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions if required
  // BYPASS: SUPER_ADMIN has full access, ignore permission checks
  const roleCode = user?.role?.code;
  const isPrivileged = roleCode === 'SUPER_ADMIN';

  if (requiredPermissions.length > 0 && !isPrivileged) {
    if (!hasAllPermissions(requiredPermissions)) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="textSecondary">
            You do not have permission to access this page.
          </Typography>
        </Box>
      );
    }
  }

  // Check roles if required
  if (requiredRoles.length > 0) {
    if (!user) return null;
    const userRoleHierarchy = resolveUserRoleLevel(user.role.code);
    const hasRequiredRole = requiredRoles.some((role) => {
      const requiredHierarchy = resolveRequiredRoleLevel(role);
      return userRoleHierarchy <= requiredHierarchy;
    });

    if (!hasRequiredRole) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Your role does not have access to this page.
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;
